import { createRoot } from "react-dom/client";
import { WidgetApp } from "./components/WidgetApp";
import { pagePromoFor, rememberLinkPromo } from "./state/link-promo";
import {
  fileClickIfUnledgered,
  getAttribution,
  scopeAttribution,
} from "./state/attribution";
import {
  scopeMarketingConsent,
  setConsentInherited,
  setMarketingConsent,
} from "./state/tracking";
import type { EmbedAttributes } from "./types";
import "./styles.css";

// Must be imported before any render so init() fires before the first
// potential throw inside WidgetApp.
import "./sentry";

/**
 * Auto-mount every <div id="vertu-widget" ...> on the page.
 *
 * Each instance reads:
 *   data-tenant        required — tenant slug (e.g. "laborie")
 *   data-widget        required — widget slug (e.g. "default")
 *   data-brand         optional — scope to one brand
 *   data-default-tab   optional — service slug to land on
 *   data-event         optional — event slug to deep-link into
 *   data-combo         optional — package (combo) slug to deep-link into
 *   data-option        optional — option (unit) slug to open with chosen
 *   data-promo         optional — promo code the checkout applies for the guest
 *
 * Also reads a page-level `?event=<slug>` query param (the public hosted
 * event page links to the widget with it) to open straight to that event,
 * and `?combo=<slug>` (newsletter package cards link with it) to open that
 * package, and `?option=<slug>` (a venue's "Book this ride" buttons) to open
 * with that option already chosen, and `?promo=<code>` (a venue's campaign
 * mail) to apply that promo code at checkout (state/link-promo.ts).
 */
function mountWidgets() {
  // Capture ad-click/campaign attribution from the landing URL up front, before
  // any host-page navigation could drop the query params.
  getAttribution();

  const nodes = document.querySelectorAll<HTMLElement>(
    '[id="vertu-widget"], [data-vertu-widget]',
  );

  // Page-level deep-link from the public hosted event page
  // (https://{tenant}.vertu.co.za?event={slug}). A per-node data-event
  // attribute takes precedence when set.
  const qs = new URLSearchParams(window.location.search);
  const eventParam = qs.get("event") || undefined;
  const comboParam = qs.get("combo")?.trim() || undefined;
  // Hosted booking page (book.vertu.co.za/?tenant=…): the whole mount is driven
  // by the query string so a tenant can hang a plain "Book now" link off any
  // page of their site. Embeds keep using data-* attributes and are unaffected —
  // an attribute always wins, so a query string can never retarget an embed
  // that a tenant has already placed on their own page.
  const q = (k: string) => qs.get(k)?.trim() || undefined;
  // `?promo=`: only for the one venue the page is for (link-promo.ts).
  const pagePromo = pagePromoFor(
    q("promo"),
    Array.from(nodes, (n) => n.dataset.tenant || q("tenant") || ""),
  );

  nodes.forEach((node) => {
    if (node.dataset.vertuMounted === "true") return;
    node.dataset.vertuMounted = "true";

    const forcedDevice =
      node.dataset.vtDevice === "mobile" || node.dataset.vtDevice === "desktop"
        ? node.dataset.vtDevice
        : undefined;
    const attrs: EmbedAttributes = {
      tenantSlug: node.dataset.tenant || q("tenant") || "",
      widgetSlug: node.dataset.widget || q("widget") || "default",
      brandSlug: node.dataset.brand || q("brand"),
      defaultTabSlug: node.dataset.defaultTab || q("tab"),
      initialEventSlug: node.dataset.event || eventParam,
      initialComboSlug: node.dataset.combo || comboParam,
      initialOptionSlug: node.dataset.option || q("option"),
      device: forcedDevice,
      forcedLayout: node.dataset.vtLayoutOverride || undefined,
    };

    if (attrs.tenantSlug) {
      rememberLinkPromo(attrs.tenantSlug, node.dataset.promo || pagePromo);
      // Re-home the staged attribution blob and the consent gate under THIS
      // tenant's keys — on the shared hosted origin a bare key is readable by
      // every venue's session.
      scopeAttribution(attrs.tenantSlug);
      scopeMarketingConsent(attrs.tenantSlug);
      // File this landing in the click ledger when nothing else did — the
      // hosted booking page has no site tag, and an embed only has one if the
      // tenant installed it. Deduped on the tag's receipt, so a tenant who DOES
      // run the tag is unaffected. Must come after scopeAttribution: the
      // dedup reads the blob under this tenant's key.
      fileClickIfUnledgered(attrs.tenantSlug);
      // Consent inheritance: the site tag sets ?vt_consent=1 only when the
      // guest ALREADY granted ad/analytics consent on the tenant's own site
      // (same data controller). Honouring it here unlocks the client-side
      // tags without a second prompt.
      //
      // It now also PRE-TICKS the checkout's marketing box (via
      // consentWasInherited), because the previous split was indefensible in
      // practice: the pixel fired on the inherited grant while the checkout
      // showed an unticked box, and a guest who left it alone had a
      // granted=false consent row written for them — a refusal they never
      // made, which then suppressed every server-side conversion. The box is
      // still shown and still untickable, so the guest keeps the last word.
      try {
        if (new URLSearchParams(window.location.search).get("vt_consent") === "1") {
          setMarketingConsent(true);
          setConsentInherited();
        }
      } catch {
        /* ignore */
      }
    }
    if (!attrs.tenantSlug) {
      console.error(
        "[vertu-widget] No tenant: set data-tenant on the mount node, or ?tenant= on a hosted page",
        node,
      );
      node.innerHTML =
        '<p style="font:14px/1.6 system-ui;padding:24px;text-align:center;color:#5A5A6E">' +
        "This booking link is missing its venue. Please check the link and try again." +
        "</p>";
      return;
    }

    // Per-embed width override. Accepts any CSS length (px / % / vw / etc.)
    // e.g. `data-width="92vw"`. Falls through to the tenant default set
    // server-side, which falls through to the CSS default (80vw, capped).
    if (node.dataset.width) {
      node.style.setProperty("--vt-widget-width", node.dataset.width);
    }

    const root = createRoot(node);
    root.render(<WidgetApp embed={attrs} />);
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", mountWidgets);
} else {
  mountWidgets();
}

// Re-scan if the host page adds widgets dynamically (SPAs, pjax, etc.)
if (typeof MutationObserver !== "undefined" && document.body) {
  new MutationObserver(() => {
    mountWidgets();
  }).observe(document.body, { childList: true, subtree: true });
}
